import React from 'react';
import { colors } from 'theme/colors';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import { Chips } from 'components/atoms/Chips';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { EPermission } from 'models/permissions';
import { CaseStatus } from 'models/cases';
import { useTranslations } from 'hooks/useTranslations';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { renderStatusCaseFieldColor } from 'pages/CaseManagementPage/helpers';

interface ILegalEntityCaseTableCard {
  data: any;
  isApplication: boolean;
  isOtherRequest: boolean;
  handleView: (id: string) => void;
  handleCreateLicence: (id: string) => void;
  handleAssign: (id: string) => void;
  canAssignCase: (id: string, statusUniqueId: string) => boolean;
  showDetailsCases: boolean;
}

const LegalEntityCaseTableCard = ({
  data,
  isApplication,
  isOtherRequest,
  handleView,
  handleCreateLicence,
  handleAssign,
  canAssignCase,
  showDetailsCases,
}: ILegalEntityCaseTableCard) => {
  const { t } = useTranslations();

  const canCreateLicence = data.statusUniqueId === CaseStatus.GRANTED;

  return (
    <Grid
      container
      columnSpacing={3}
      sx={{
        backgroundColor: colors.gray100,
        border: `1px solid ${colors.gray200}`,
        borderRadius: '4px',
        overflow: 'hidden',
        padding: '20px',
        rowGap: 3,
      }}
    >
      <Grid size={{ xs: 12, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('caseManagement.cases.caseId.label')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.caseNumber)}</Typography>
      </Grid>

      {isOtherRequest ? (
        <Grid size={{ xs: 12, lg: 3 }}>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t('caseManagement.cases.licenceTaskName.label')}
          </Typography>
          <Typography variant="body2">{emptyFieldHolder(data.licenceTaskName)}</Typography>
        </Grid>
      ) : (
        <Grid size={{ xs: 12, lg: 3 }}>
          <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
            {t('caseManagement.cases.taskName.label')}
          </Typography>
          <Typography variant="body2">
            {emptyFieldHolder(`${data.licenceTemplateCode} ${data.licenceTemplateName}`)}
          </Typography>
        </Grid>
      )}
      <Grid size={{ xs: 12, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('caseManagement.cases.createDate.label')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.createdDate)}</Typography>
      </Grid>

      <Grid size={{ xs: 12, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('caseManagement.cases.status.label')}
        </Typography>
        <Chips text={data.status} variant={renderStatusCaseFieldColor(data)} fullWidth />
      </Grid>

      <Grid size={{ xs: 12, lg: 3 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('caseManagement.cases.assignedTo.label')}
        </Typography>
        <Typography variant="body2">
          {data.assignedTo ? `${data.assignedTo} (${data.assignedToDisplayUniqueId})` : '-'}
        </Typography>
      </Grid>

      {showDetailsCases && (
        <Grid
          container
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'end',
            borderTop: {
              xs: `1px solid ${colors.primary100}`,
              lg: 'none',
            },
          }}
          spacing={1}
        >
          <Grid size={{ xs: 12, sm: 4 }}>
            <Button
              id={`legal_entity_details_case-view-${data.id}`}
              variant={EButtonVariants.outlined}
              onClick={() => handleView(data.id as string)}
              permission={EPermission.VIEW_CASES_LE}
              label={t('caseManagement.cases.actions.details.label')}
              fullWidth
              size={EButtonSizes.small}
            />
          </Grid>

          {isApplication && canCreateLicence && (
            <Grid size={{ xs: 12, sm: 4 }}>
              <Button
                id={`legal_entity_details_case-create-licence-${data.id}`}
                variant={EButtonVariants.outlined}
                onClick={() => handleCreateLicence(data.id as string)}
                permission={EPermission.CREATE_MANAGE_LICENCE}
                label={t('caseManagement.cases.actions.createLicence.label')}
                fullWidth
                size={EButtonSizes.small}
              />
            </Grid>
          )}
          {canAssignCase(data.licenceAuthorityUniqueId, data.statusUniqueId) && (
            <Grid size={{ xs: 12, sm: 4 }}>
              <Button
                id={`legal_entity_details_case-assign-${data.id}`}
                variant={EButtonVariants.outlined}
                onClick={() => handleAssign(data.id as string)}
                permission={EPermission.CREATE_MANAGE_LICENCE}
                label={t('caseManagement.cases.actions.assignTo.label')}
                fullWidth
                size={EButtonSizes.small}
              />
            </Grid>
          )}
        </Grid>
      )}
    </Grid>
  );
};

export { LegalEntityCaseTableCard };
